import { Guest } from '../types/interfaces';
import { defaultIlsPerEur, defaultUsdPerEur } from './exchangeRate';

export { parseGiftAmount } from './parseGiftAmount';

export function computeGiftValueEur(
  giftEur?: number,
  giftIls?: number,
  ilsPerEur: number = defaultIlsPerEur(),
): number {
  return (giftEur ?? 0) + (giftIls ?? 0) / ilsPerEur;
}

export function computeGiftValueIls(
  giftEur?: number,
  giftIls?: number,
  ilsPerEur: number = defaultIlsPerEur(),
): number {
  return computeGiftValueEur(giftEur, giftIls, ilsPerEur) * ilsPerEur;
}

export function computeGiftValueUsd(
  giftEur?: number,
  giftIls?: number,
  ilsPerEur: number = defaultIlsPerEur(),
  usdPerEur: number = defaultUsdPerEur(),
): number {
  return computeGiftValueEur(giftEur, giftIls, ilsPerEur) * usdPerEur;
}

export function deriveGroupSide(guests: { side?: string }[]): string {
  const first = guests.find(g => g.side);
  return first?.side || '-';
}

export function hasGift(guest: Guest): boolean {
  return (
    (guest.giftEur != null && guest.giftEur > 0) ||
    (guest.giftIls != null && guest.giftIls > 0) ||
    guest.giftPhysical === true
  );
}

export function shouldShowInGiftsTable(guest: Guest): boolean {
  return guest.coming === true || hasGift(guest);
}

export function shouldShowGroupInGiftsTable(guests: Guest[]): boolean {
  return guests.some(shouldShowInGiftsTable);
}

export function formatCompactGift(guest: Guest): string | undefined {
  const parts: string[] = [];
  if (guest.giftEur != null && guest.giftEur > 0) parts.push(formatMoney(guest.giftEur, 'EUR'));
  if (guest.giftIls != null && guest.giftIls > 0) parts.push(formatMoney(guest.giftIls, 'ILS'));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export function sumGroupGiftEur(guests: Guest[]): number {
  return guests.reduce((s, g) => s + (g.giftEur ?? 0), 0);
}

export function sumGroupGiftIls(guests: Guest[]): number {
  return guests.reduce((s, g) => s + (g.giftIls ?? 0), 0);
}

export function groupHasSplitGifts(guests: Guest[]): boolean {
  return guests.filter(g => (g.giftEur ?? 0) > 0 || (g.giftIls ?? 0) > 0).length > 1;
}

export type GroupThankedStatus = 'Yes' | 'No' | 'Partial' | '—';

export function groupThankedStatus(guests: Guest[]): GroupThankedStatus {
  const relevant = guests.filter(g => hasGift(g));
  if (relevant.length === 0) return '—';
  const thankedCount = relevant.filter(g => g.thanked === true).length;
  const notThankedCount = relevant.filter(g => g.thanked === false).length;
  if (thankedCount === relevant.length) return 'Yes';
  if (notThankedCount === relevant.length) return 'No';
  if (notThankedCount > 0) return 'Partial';
  return '—';
}

export function formatMoney(
  amount: number | undefined,
  currency: 'EUR' | 'ILS' | 'USD',
): string {
  if (amount == null || amount <= 0) return '—';
  const symbol = currency === 'EUR' ? '€' : currency === 'ILS' ? '₪' : '$';
  return `${symbol}${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}
